/**
 * DuoFeedCard — chronological activity feed across all joint trips.
 *
 * Reads GET /api/partner/duo-feed (combined timeline across all active
 * shared_trip_links). Tapping a row opens the SharedTripTimeline for that
 * specific trip so the partners can keep going.
 *
 * Also renders a compact "Shared trips" list below the feed so the user can
 * jump directly into any joint trip's timeline, even ones with no activity
 * yet.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Heart, MessageSquareText, Sparkles, Flag, Pin, ArrowRight, Loader2, MapPin } from "lucide-react";
import SharedTripTimeline from "./SharedTripTimeline";

const KIND_ICON = {
  note: Heart, milestone: Flag, reaction: Sparkles, photo_ref: Pin,
};

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const s = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function DuoFeedCard() {
  const { user } = useAuth();
  const [feed, setFeed] = useState([]);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openTrip, setOpenTrip] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [feedRes, tripsRes] = await Promise.all([
        axios.get(`${API}/partner/duo-feed?limit=20`),
        axios.get(`${API}/partner/shared-trips`),
      ]);
      setFeed(feedRes.data?.events || []);
      setTrips(tripsRes.data?.shared_trips || []);
    } catch {
      // Silent — feed is optional; CoupleDashboard handles the "no partner" branch
      setFeed([]);
      setTrips([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const openTimeline = (trip) => setOpenTrip(trip);
  const handleClose = () => { setOpenTrip(null); refresh(); };

  return (
    <>
      <Card data-testid="duo-feed-card" className="border-pink-500/20">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <MessageSquareText className="w-4 h-4 text-pink-500" />
            Duo feed
            {feed.length > 0 && <Badge variant="outline" className="text-[10px]">{feed.length}</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {loading && (
            <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
          )}

          {!loading && feed.length === 0 && trips.length === 0 && (
            <p className="text-sm text-muted-foreground py-2">
              No shared trips yet. Tap <span className="text-pink-500 font-medium">Manage shared trips</span> above to mark the adventures you took together.
            </p>
          )}

          {!loading && feed.length === 0 && trips.length > 0 && (
            <p className="text-sm text-muted-foreground py-1">
              No posts yet — open any shared trip below to start the timeline.
            </p>
          )}

          {!loading && feed.map((e) => {
            const Icon = KIND_ICON[e.event_type] || Heart;
            const trip = trips.find((t) => t.id === e.item_id) || { id: e.item_id, title: e.item_title };
            return (
              <button
                key={e.id}
                type="button"
                onClick={() => openTimeline(trip)}
                className="w-full text-left border border-border hover:border-pink-500/40 rounded-lg p-2.5 bg-card transition-colors"
                data-testid={`duo-feed-row-${e.id}`}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <Icon className="w-3.5 h-3.5 text-pink-500" />
                  <span className="text-sm font-medium truncate">{e.actor_name}</span>
                  <span className="text-[11px] text-muted-foreground">on</span>
                  <span className="text-sm truncate">{e.item_title || "Shared trip"}</span>
                  {e.item_country_code && (
                    <span className="text-[11px] text-muted-foreground inline-flex items-center">
                      <MapPin className="w-3 h-3 mr-0.5" /> {e.item_country_code}
                    </span>
                  )}
                  <span className="text-[11px] text-muted-foreground ml-auto">{timeAgo(e.created_at)}</span>
                </div>
                <p className="text-sm mt-1 text-foreground/90 line-clamp-2 whitespace-pre-wrap">
                  {e.emoji ? <span className="mr-1">{e.emoji}</span> : null}
                  {e.content}
                </p>
              </button>
            );
          })}

          {/* Shared trips list — always render if any exist */}
          {!loading && trips.length > 0 && (
            <div className="pt-2 border-t border-border/50 mt-2">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">Shared trips ({trips.length})</p>
              <div className="space-y-1.5">
                {trips.slice(0, 8).map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => openTimeline(t)}
                    className="w-full text-left flex items-center gap-2 text-sm p-1.5 rounded-md hover:bg-pink-500/10 transition-colors"
                    data-testid={`duo-feed-trip-${t.id}`}
                  >
                    <Heart className="w-3 h-3 text-pink-500 flex-shrink-0" />
                    <span className="truncate flex-1">{t.title}</span>
                    {t.country_code && <Badge variant="outline" className="text-[10px]">{t.country_code}</Badge>}
                    <ArrowRight className="w-3 h-3 text-muted-foreground" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {!loading && (feed.length > 0 || trips.length > 0) && (
            <Button
              size="sm" variant="ghost" onClick={refresh}
              className="w-full text-xs text-muted-foreground"
              data-testid="duo-feed-refresh"
            >
              Refresh
            </Button>
          )}
        </CardContent>
      </Card>

      <SharedTripTimeline
        open={Boolean(openTrip)}
        trip={openTrip}
        currentUserId={user?.id}
        onClose={handleClose}
      />
    </>
  );
}
